import { ConditionalWrapper } from "@/lib/utils";
import Button from "@components/library/Button";
import { LoadingIcon } from "@components/library/Icons";
import KeyboardShortcut from "@components/library/KeyboardShortcut";
import Tooltip from "@components/library/Tooltip";
import { UNSET_FEEDBACK } from "lib/constants";
import { SUGGESTED_LABELS } from "lib/feedback";
import { useRequireAuth } from "lib/use-auth";
import { useHotkeys } from "react-hotkeys-hook";
import { HumanEvaluator } from "@/types/app/evaluator";
import { EvaluatorLog, Log } from "@/types/app/log";
import { createEvaluatorLogAndFetchLog } from "@/services/evaluators.service";

interface FeedbackButtonsProps {
  evaluator: HumanEvaluator;
  logs: Log[] | null; // The feedbacks for this particular feedback type only.
  addingFeedback: boolean;
  setAddingFeedback: (addingFeedback: boolean) => void;
  onAddFeedback: (evaluatorLog: EvaluatorLog) => void;
  logId: string;
}

/* 
  An array of feedback buttons for a particular feedback type.
*/
const FeedbackButtons = ({
  evaluator,
  logs,
  addingFeedback,
  setAddingFeedback,
  onAddFeedback,
  logId,
}: FeedbackButtonsProps) => {
  const { user } = useRequireAuth();

  if (!user) {
    return null;
  }

  const options = evaluator.spec.options;
  if (options === null || options === undefined) {
    console.log("Feedback type values is null", options, evaluator);
    throw new Error("Feedback type values is null");
  }

  const latestFeedbackLog =
    logs?.length && logs?.length > 0
      ? logs?.toSorted((a, b) => (b.created_at.isAfter(a.created_at) ? 1 : -1))[0]
      : null;

  const currentOptions =
    latestFeedbackLog && "judgment" in latestFeedbackLog
      ? Array.isArray(latestFeedbackLog.judgment)
        ? latestFeedbackLog.judgment
        : [latestFeedbackLog.judgment]
      : [];

  return (
    <div className="flex min-w-0 flex-wrap items-center gap-8 truncate">
      {options // Iterating over the 'labels' for the group.
        // The UNSET_FEEDBACK value is no longer used, but might be around on old projects
        .filter(({ name }) => name !== UNSET_FEEDBACK)
        .map(({ name: label }) => {
          const selected = currentOptions !== undefined && currentOptions.includes(label);

          const handleClick = async (allowToggleOff: boolean = false) => {
            if (addingFeedback) {
              return;
            }
            // If the button is already selected, and we don't allow toggling off, then do nothing.
            if (selected && !allowToggleOff) {
              return;
            }
            setAddingFeedback(true);
            let createdLog: EvaluatorLog;
            // Toggles on and off
            if (selected && allowToggleOff) {
              if (evaluator.spec.return_type === "multi_select") {
                createdLog = await createEvaluatorLogAndFetchLog(
                  {
                    id: evaluator.id,
                    parent_id: logId,
                    judgment: currentOptions.filter((l) => l !== label),
                    user: user.email_address,
                  },
                  evaluator.version_id,
                );
              } else {
                createdLog = await createEvaluatorLogAndFetchLog(
                  {
                    id: evaluator.id,
                    parent_id: logId,
                    judgment: null,
                    user: user.email_address,
                  },
                  evaluator.version_id,
                );
              }
            } else {
              if (evaluator.spec.return_type === "multi_select") {
                createdLog = await createEvaluatorLogAndFetchLog(
                  {
                    id: evaluator.id,
                    parent_id: logId,
                    judgment: [...currentOptions, label],
                    user: user.email_address,
                  },
                  evaluator.version_id,
                );
              } else {
                createdLog = await createEvaluatorLogAndFetchLog(
                  {
                    id: evaluator.id,
                    parent_id: logId,
                    judgment: label,
                    user: user.email_address,
                  },
                  evaluator.version_id,
                );
              }
            }
            try {
              // We only add one feedback above.
              onAddFeedback(createdLog);
            } finally {
              setAddingFeedback(false);
            }
          };

          return (
            <FeedbackButton
              key={label}
              label={label}
              evaluator={evaluator}
              selected={selected}
              handleClick={handleClick}
            />
          );
        })}
      {addingFeedback && <LoadingIcon className="h-16 w-16 animate-spin" />}
    </div>
  );
};

interface FeedbackButtonProps {
  evaluator: HumanEvaluator;
  handleClick: (allowToggleOff: boolean) => void;
  selected: boolean;
  label: string;
}

export const FeedbackButton = ({
  evaluator,
  label, // The label for this specific button
  selected,
  handleClick,
}: FeedbackButtonProps) => {
  // @ts-ignore -- this one is too much of a mess.
  const defaultBehaviour = SUGGESTED_LABELS[evaluator.name]?.find((valueObj) => valueObj.value === label);
  const Icon = defaultBehaviour?.Icon;
  const keyboardShortcut = defaultBehaviour?.keyboardShortcut;

  const sentiment = evaluator.spec.options?.find(({ name }) => name === label)?.valence;
  const shade = sentiment === "positive" ? "green" : sentiment === "negative" ? "red" : "blue";

  useHotkeys(
    keyboardShortcut || "", // Important! Undefined causes this to be triggered on every keypress.
    () => {
      handleClick(false);
    },
    { enabled: keyboardShortcut !== undefined },
    [keyboardShortcut, handleClick],
  );

  return (
    <ConditionalWrapper
      condition={keyboardShortcut}
      wrapper={(children) => (
        <Tooltip key={label} content={<KeyboardShortcut>{keyboardShortcut}</KeyboardShortcut>} side="top">
          {children}
        </Tooltip>
      )}
    >
      <Button
        key={label}
        shade={selected && sentiment ? shade : "gray"}
        active={selected}
        IconLeft={Icon}
        onClick={() => handleClick(true)}
        className="min-w-0"
      >
        <span className="truncate whitespace-nowrap">{label}</span>
      </Button>
    </ConditionalWrapper>
  );
};

export default FeedbackButtons;
